const DEBUGGING: boolean = process.env.NODE_ENV === 'development'

import { BaseObject, MongoObject } from './_baseClasses'
import * as telegram from "./telegram"
import * as utils_time from "./utils_time"

interface UserChallenge {
  name: string,
  expirationDate: Date,
  numUsersCompleted: number,
}

interface Snapshot {
  date: Date,
  numUsersCompleted: number,
  costToComplete: number
}

// client only
interface GraphData {
  days: number[]
  values: number[]
  truncatedBeforeZero: boolean
}

export class Challenge extends MongoObject {
  protected static collectionName: string = 'challenges'           // IMPORTANT

  public topShotGUID: string = null             // e.g. 1a41dd5d-552e-4bd0-9a77-42c55eb41532
  public userChallenge: UserChallenge = null    // all the goodies are in here
  public acronym: string = null                 // e.g. RS (populated manually by Alex using Compass)
  public active: boolean = null                 // just so we can fetch from DB fast
  public snapshots: Snapshot[] = null
  public lastNumCompleted: number = null        // added 05/25/21 because Top Shot API started returning numUsersCompleted as 0 for active challenges
  public partialCompletions: number[] = null    // e.g. partialCompletions[3] === 512 means 512 people have 3/X slots filled

  // complex, class-based fields (require to be hydrated after loading from external sources like mongo or elastic)

  // client side only
  public graphData: GraphData = null

  constructor(po?: object) {
    super()
    if (po) this.injectPlainObject(po)
    this.hydrate()
  }

  protected hydrate(): void {
    super.hydrate()
    if (this.userChallenge && typeof this.userChallenge.expirationDate === 'string') this.userChallenge.expirationDate = new Date(this.userChallenge.expirationDate)
  }

  public isSafe(): boolean {
    return true
  }

  // client side only
  public generateGraphData() {
    if (this.graphData) return
    if (!this.snapshots || !this.snapshots.length) return
    const nowTimeSec: number = new Date().getTime() / 1000
    const graphData: GraphData = {
      days: [],
      values: [],
      truncatedBeforeZero: false
    }

    const challengeEndInMS: number = this.userChallenge.expirationDate.getTime()
    const cutoffInMS: number = challengeEndInMS + 7 * 86400 * 1000        // show 7 days of data after challege end

    for (const snapshot of this.snapshots) {
      if (snapshot.date.getTime() > cutoffInMS) {
        graphData.truncatedBeforeZero = true
        break
      }
      const secsAgo: number = nowTimeSec - snapshot.date.getTime() / 1000
      const daysAgo: number = secsAgo / 86400
      graphData.days.push(-daysAgo)
      graphData.values.push(snapshot.costToComplete)
    }

    this.graphData = graphData
  }

  // https://plotly.com/javascript/line-charts/
  public getPlotlyData() {
    this.generateGraphData()
    const x: number[] = []
    const y: number[] = []
    for (const [i, day] of this.graphData.days.entries()) {
      x.push(day)
      y.push(this.graphData.values[i])
    }
    const trace = { x, y, type: 'scatter', mode: 'lines', line: { shape: 'spline' } }
    return [trace]
  }
  public getPlotyLayout() {
    console.log(`getPlotyLayout() for ${this.userChallenge.name}`)
    this.generateGraphData()
    const layout: any = {
      width: 260, height: 150,
      margin: { l: 32, r: 7, t: 0, b: 21 },
      linecolor: "#444",
      gridcolor: "#eee",
      zerolinecolor: "#eee",
      yaxis: {
        // rangemode: 'tozero',
        tickprefix: '$',
        tickfont: { size: 10 }
      },
      xaxis: {
        // rangemode: 'tozero',
        ticksuffix: 'd',
        tickfont: { size: 10 }
      },
      hoverlabel: {
        font: { size: 10 }
      }
    }

    if (!this.graphData.truncatedBeforeZero) layout.xaxis.rangemode = 'tozero'
    return layout
  }

  public getOldestDay(): number {
    this.generateGraphData()
    if (!this.graphData) return null
    if (this.graphData.days.length === 0) return null
    return this.graphData.days[0]
  }

  public getPlotlyTitle(): string {
    const nowTimeMS: number = new Date().getTime()
    if (this.active) {
      const daysAgoStart: number = -Math.round(this.graphData.days[0])
      if (daysAgoStart === 0) {
        const minsAgoStart: number = Math.round((nowTimeMS - this.snapshots[0].date.getTime()) / 60000)
        if (minsAgoStart > 60) {
          const hoursAgoStart = Math.round(minsAgoStart / 60)
          return `Cost since starting ${hoursAgoStart} hour${hoursAgoStart === 1 ? '' : 's'} ago`
        }
        return `Cost since starting ${minsAgoStart} minutes ago`
      }
      return `Cost since starting ${daysAgoStart} day${daysAgoStart === 1 ? '' : 's'} ago`
    }
    if (this.graphData.truncatedBeforeZero) return `From start to 7 days after end`
    const dayAgoEnded: number = Math.round((nowTimeMS - this.userChallenge.expirationDate.getTime()) / 86400000)
    if (dayAgoEnded === 0) return `Cost from beginning to end`
    return `From start to ${dayAgoEnded} day${dayAgoEnded === 1 ? '' : 's'} after end`
  }


  // https://plotly.com/javascript/line-charts/
  public getPlotlyCompletionsData() {
    const x: string[] = []
    const y: number[] = []
    const fullNumber: number = this.partialCompletions.length - 1
    for (let i = 2; i <= fullNumber; i++) {
      x.push(`${i}/${fullNumber}`)
      y.push(this.partialCompletions[i])
    }
    const trace = { x, y, type: 'bar', text: y, textposition: 'auto' }
    return [trace]
  }

  public getPlotyCompletionsLayout() {
    const layout: any = {
      width: 260, height: 150,
      margin: { l: 28, r: 7, t: 0, b: 21 },
      linecolor: "#444",
      gridcolor: "#eee",
      zerolinecolor: "#eee",
      yaxis: {
        tickfont: { size: 10 },
        ticksuffix: '',
      },
      xaxis: {
        tickfont: { size: 10 }
      },
      hoverlabel: {
        font: { size: 10 }
      }
    }

    return layout
  }

  public getPlotlyCompletionsTitle(): string {
    return `Completions by # of Moments`
  }


}




(async () => {
})();
